import { useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  useUserHasAccess,
  useCanManageHoursMonthlyOverview,
} from "../../../routes/roleOnly";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { HoursViewComponent } from "../../../components/dashboard/hours/hours_view_component";
import { HoursMobileActionsNav } from "../../../components/dashboard/hours/hours-mobile-actions-nav";
import {
  HoursQuickEntry,
} from "../../../components/dashboard/hours/quick/HoursQuickEntry";
import {
  readEntryMode,
  writeEntryMode,
} from "../../../components/dashboard/hours/quick/quickEntryUtils";
import { useSearchParams, useParams } from "react-router-dom";
import { format, startOfWeek, endOfWeek } from "date-fns";
import toast from "react-hot-toast";
import { HourEditDialog } from "../../../components/dashboard/hours/hour_edit_dialog";
import { HoursSheetEmployeePicker } from "../../../components/dashboard/hours/sheet/HoursSheetEmployeePicker";
import { exportHoursExcel, exportHoursPdf } from "../../../api/hour";
import { HOURS_SHEET_EMPLOYEE_FILTERS } from "../../../components/dashboard/hours/hoursEmployeeFilters";
import "../../../components/dashboard/hours/quick/hours-quick-entry.css";
import "../../../components/dashboard/hours/sheet/hours-sheet.css";

const LG_QUERY = "(min-width: 1024px)";
/** Stable filters ref — avoid recreating array each render (would refetch employee list). */
const EMPLOYEE_PICKER_FILTERS = HOURS_SHEET_EMPLOYEE_FILTERS;

export const HourSheet = () => {
  const currentUser = useSelector((state) => state.session.currentUser);
  const [user, setUser] = useState(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeHourId } = useParams();

  const isAdmin = useSelector((state) =>
    state.session.currentUser?.roles?.some((r) => r.name === "admin")
  );
  const isManager = useUserHasAccess({ role: "hours", action: "manage" });
  const canEditHours = useUserHasAccess({ role: "edit_hours" });
  const isHoursHandler = useCanManageHoursMonthlyOverview();
  // Matcher API batch_upsert: admin, edit_hours, hours_handler (eller hours+manage).
  const canPickEmployee = Boolean(
    isAdmin || isManager || canEditHours || isHoursHandler
  );

  const userMemo = useMemo(
    () => (canPickEmployee ? user : currentUser),
    [user, canPickEmployee, currentUser]
  );
  const user_id = userMemo?.id;

  const [hourFormId, setHourFormId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newHourPrefill, setNewHourPrefill] = useState(null);
  const [lastChangedHour, setLastChangedHour] = useState(null);
  const [deletedHourId, setDeletedHourId] = useState(null);
  const [currentViewDate, setCurrentViewDate] = useState(null);
  const [currentView, setCurrentView] = useState(() => searchParams.get("view") || "day");
  const [entryMode, setEntryMode] = useState(() => readEntryMode());
  const [isLg, setIsLg] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(LG_QUERY).matches : false
  );
  const [weekHoursLabel, setWeekHoursLabel] = useState(null);
  const [exportScope, setExportScope] = useState(() => ({
    date_from: searchParams.get("from") || null,
    date_to: searchParams.get("to") || null,
    date: searchParams.get("date") || null,
    view: searchParams.get("view") || "day",
  }));
  const [exporting, setExporting] = useState(null);
  const { t } = useTranslation();

  const hoursViewRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(LG_QUERY);
    const onChange = () => setIsLg(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const showQuick = isLg && entryMode === "quick";

  const handleEntryModeChange = useCallback((mode) => {
    setEntryMode(mode);
    writeEntryMode(mode);
  }, []);

  const hourIdParam = useMemo(() => searchParams.get("hourId"), [searchParams]);

  const clearHourIdFromUrl = useCallback(() => {
    const current = Object.fromEntries(searchParams.entries());
    if (current.hourId) {
      delete current.hourId;
      setSearchParams(current, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (routeHourId && /^\d+$/.test(String(routeHourId))) {
      setHourFormId(String(routeHourId));
      setDialogOpen(true);
    }
  }, [routeHourId]);

  useEffect(() => {
    if (routeHourId && /^\d+$/.test(String(routeHourId))) return;
    if (hourIdParam && /^\d+$/.test(hourIdParam)) {
      setHourFormId(hourIdParam);
      setDialogOpen(true);
    }
  }, [hourIdParam, routeHourId]);

  const initialViewParam = useMemo(
    () => searchParams.get("view") || "day",
    [searchParams]
  );
  const initialDateParam = useMemo(
    () => searchParams.get("date") || null,
    [searchParams]
  );
  const initialFromParam = useMemo(
    () => searchParams.get("from") || null,
    [searchParams]
  );
  const initialToParam = useMemo(
    () => searchParams.get("to") || null,
    [searchParams]
  );
  const initialPeriodParam = useMemo(
    () => searchParams.get("period") || "month",
    [searchParams]
  );

  const updateUrlParams = useCallback(
    (updates) => {
      const current = Object.fromEntries(searchParams.entries());
      const next = { ...current, ...updates };
      if (next.view === "day" || next.view === "week") {
        delete next.from;
        delete next.to;
        if (!next.date) delete next.date;
      }
      if (next.view === "period") {
        delete next.date;
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleHourClick = useCallback(
    (hourId) => {
      setHourFormId(hourId);
      setNewHourPrefill(null);
      setDialogOpen(true);
      updateUrlParams({ hourId });
    },
    [updateUrlParams]
  );

  const handleNewHour = useCallback(
    (date = null) => {
      setHourFormId(null);
      setDialogOpen(true);
      const d =
        date ||
        currentViewDate ||
        initialDateParam ||
        new Date().toISOString().substring(0, 10);
      setNewHourPrefill({
        date: d,
        user_id: userMemo?.id,
        user: userMemo,
      });
    },
    [currentViewDate, initialDateParam, userMemo]
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setHourFormId(null);
    setNewHourPrefill(null);
    clearHourIdFromUrl();
  }, [clearHourIdFromUrl]);

  const handleSaved = useCallback(
    (saved) => {
      setLastChangedHour(saved);
      closeDialog();
    },
    [closeDialog]
  );

  const handleDeleted = useCallback(
    (id) => {
      setDeletedHourId(id);
      setTimeout(() => setDeletedHourId(null), 0);
      closeDialog();
    },
    [closeDialog]
  );

  const resolveExportRange = useCallback(() => {
    if (exportScope?.date_from && exportScope?.date_to) {
      return { date_from: exportScope.date_from, date_to: exportScope.date_to };
    }
    const anchor = currentViewDate || initialDateParam || new Date().toISOString().substring(0, 10);
    const d = new Date(anchor);
    if (currentView === "day") {
      const day = format(d, "yyyy-MM-dd");
      return { date_from: day, date_to: day };
    }
    return {
      date_from: format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd"),
      date_to: format(endOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd"),
    };
  }, [exportScope, currentViewDate, initialDateParam, currentView]);

  const handleExport = useCallback(
    async (kind) => {
      if (exporting || !user_id) return;
      const { date_from, date_to } = resolveExportRange();
      const params = {
        date_from,
        date_to,
        filters: [{ name: "USER", value: user_id }],
      };
      setExporting(kind);
      try {
        if (kind === "pdf") {
          await exportHoursPdf(params);
          toast.success(t("hours.sheet.export_pdf_success"));
        } else {
          await exportHoursExcel(params);
          toast.success(t("hours.sheet.export_excel_success"));
        }
      } catch {
        toast.error(t("hours.sheet.export_error"));
      } finally {
        setExporting(null);
      }
    },
    [exporting, user_id, resolveExportRange, t]
  );

  const handleScopeChange = useCallback(
    (view) => {
      if (entryMode === "quick") {
        handleEntryModeChange("form");
      }
      setCurrentView(view);
      hoursViewRef.current?.setView?.(view);
      updateUrlParams({ view });
    },
    [entryMode, handleEntryModeChange, updateUrlParams]
  );

  const pageHeader = (
    <div className="hts-header hts-header--dash">
      <div>
        <div className="hts-eyebrow">{t("hours.sheet.eyebrow")}</div>
        <h1 className="hts-title">{t("Timeliste")}</h1>
        {canPickEmployee ? (
          <HoursSheetEmployeePicker
            selected={userMemo}
            weekHoursLabel={weekHoursLabel}
            filters={EMPLOYEE_PICKER_FILTERS}
            onChange={(nextUser) => {
              setUser(nextUser);
              setHourFormId(null);
              setDialogOpen(false);
              if (nextUser?.id) {
                updateUrlParams({ user_id: nextUser.id });
              }
            }}
          />
        ) : null}
      </div>

      <div className="hts-header-actions">
        <div className="hts-seg" role="group" aria-label={t("hours.sheet.scope_aria")}>
          {[
            { id: "week", label: t("Week") },
            { id: "day", label: t("Day") },
            { id: "period", label: t("Period") },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              className={!showQuick && currentView === s.id ? "is-active" : ""}
              onClick={() => handleScopeChange(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {isLg && (
          <div className="hts-seg" role="group" aria-label={t("hours.quick.mode_aria")}>
            <button
              type="button"
              className={entryMode === "quick" ? "is-active" : ""}
              onClick={() => handleEntryModeChange("quick")}
            >
              {t("hours.quick.mode_quick")}
            </button>
            <button
              type="button"
              className={entryMode === "form" ? "is-active" : ""}
              onClick={() => handleEntryModeChange("form")}
            >
              {t("hours.quick.mode_form")}
            </button>
          </div>
        )}

        <div className="hts-export" role="group" aria-label={t("hours.sheet.export_aria")}>
          <button
            type="button"
            className="hts-export-btn"
            disabled={!!exporting || !user_id}
            onClick={() => handleExport("pdf")}
          >
            {exporting === "pdf" ? t("hours.sheet.export_pdf_loading") : t("hours.sheet.export_pdf")}
          </button>
          <button
            type="button"
            className="hts-export-btn"
            disabled={!!exporting || !user_id}
            onClick={() => handleExport("excel")}
          >
            {exporting === "excel"
              ? t("hours.sheet.export_excel_loading")
              : t("hours.sheet.export_excel")}
          </button>
        </div>

        {!showQuick && (
          <button
            type="button"
            className="hts-add-btn hidden sm:inline-flex"
            onClick={() => handleNewHour()}
          >
            <span className="hts-plus">+</span>
            {t("hours.sheet.add_hour")}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="hts">
      <DashboardHeader
        title={t("Timeliste")}
        variant="v2"
        customHeader={pageHeader}
      >
        <div className="hts-page">
          <div className="hts-page-inner">
            {showQuick ? (
              <HoursQuickEntry
                key={`quick-${user_id}`}
                user={userMemo}
                userId={user_id}
                initialDate={currentViewDate || initialDateParam}
                onOpenHour={handleHourClick}
                refreshToken={lastChangedHour || deletedHourId}
              />
            ) : (
              <HoursViewComponent
                ref={hoursViewRef}
                key={`hours-view-${user_id}`}
                user_id={user_id}
                onHourClick={handleHourClick}
                onNewHourClick={handleNewHour}
                showFilters={true}
                layout="uke-strip"
                initialView={initialViewParam}
                initialDate={initialDateParam}
                initialDateFrom={initialFromParam}
                initialDateTo={initialToParam}
                initialPeriod={initialPeriodParam}
                onStateChange={({ view, date, date_from, date_to, period_type }) => {
                  if (date) setCurrentViewDate(date);
                  if (view) setCurrentView(view);
                  setExportScope({
                    date_from,
                    date_to,
                    date,
                    view,
                  });
                  if (view === "period") {
                    updateUrlParams({
                      view,
                      from: date_from,
                      to: date_to,
                      period: period_type,
                    });
                  } else {
                    updateUrlParams({ view, date });
                  }
                }}
                onWeekHoursLabel={setWeekHoursLabel}
                pushHour={lastChangedHour}
                removeHourId={deletedHourId}
              />
            )}

            <HourEditDialog
              open={dialogOpen}
              hourId={hourFormId}
              prefill={hourFormId ? null : newHourPrefill}
              onClose={closeDialog}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
            />
          </div>
        </div>

        {!showQuick && (
          <HoursMobileActionsNav
            currentView={currentView}
            onViewChange={(view) => {
              setCurrentView(view);
              hoursViewRef.current?.setView(view);
            }}
            onGoToToday={() => hoursViewRef.current?.goToToday()}
            onAddHour={() => handleNewHour()}
          />
        )}
      </DashboardHeader>
    </div>
  );
};
